"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.langgraphAdapter = void 0;
const helpers_1 = require("../helpers");
const zod_1 = require("zod");
// Detection schemas for LangChain/LangGraph formats
// LangChain message with type field (no role)
const LangChainMessageSchema = zod_1.z.array(zod_1.z.looseObject({
    type: zod_1.z.enum(["human", "ai", "tool", "system"]),
    content: zod_1.z.any(),
}));
// LangGraph message with additional_kwargs
const LangGraphMessageSchema = zod_1.z
    .array(zod_1.z.looseObject({
    role: zod_1.z.string().optional(),
    additional_kwargs: zod_1.z
        .looseObject({
        tool_calls: zod_1.z.array(zod_1.z.any()).optional(),
    })
        .optional(),
}))
    .refine((data) => {
    const hasLangGraphMessageShape = data.every((msg) => typeof msg === "object" &&
        msg !== null &&
        ("role" in msg || "additional_kwargs" in msg));
    if (!hasLangGraphMessageShape)
        return false;
    // Reject if any message has top-level parts (Microsoft Agent/Gemini format)
    return !data.some((msg) => typeof msg === "object" &&
        msg !== null &&
        "parts" in msg &&
        Array.isArray(msg.parts));
}, { message: "Messages with top-level parts are not LangGraph format" });
// Wrapped messages format
const LangGraphWrappedSchema = zod_1.z.looseObject({
    messages: zod_1.z.array(zod_1.z.any()),
});
/**
 * Detect if a message is a tool definition (not a tool result)
 * LangGraph format: {role: "tool", content: {type: "function", function: {...}}}
 * Tool results have tool_call_id, tool definitions do not
 */
function isLangGraphToolDefinition(msg) {
    if (!msg || typeof msg !== "object")
        return false;
    const message = msg;
    return (message.role === "tool" &&
        typeof message.content === "object" &&
        message.content !== null &&
        !Array.isArray(message.content) &&
        message.content.type === "function" &&
        !!message.content.function &&
        !message.tool_call_id // Tool definitions don't have tool_call_id
    );
}
// extract tool definitions from messages array
function extractToolDefinitions(messages) {
    return messages.filter(isLangGraphToolDefinition).map((msg) => {
        const message = msg;
        const func = message.content
            .function;
        const toolDef = {
            name: func.name || "",
        };
        if (func.description !== null && func.description !== undefined) {
            toolDef.description = func.description;
        }
        if (func.parameters !== null && func.parameters !== undefined) {
            toolDef.parameters = func.parameters;
        }
        return toolDef;
    });
}
function normalizeMessage(msg) {
    if (!msg || typeof msg !== "object")
        return {};
    let normalized = (0, helpers_1.removeNullFields)(msg);
    // LangGraph/LangChain: "type" field → "role" field
    if (normalized.type && !normalized.role) {
        const typeToRole = {
            human: "user",
            ai: "assistant",
            tool: "tool",
            system: "system",
        };
        const type = String(normalized.type);
        if (typeToRole[type]) {
            normalized.role = typeToRole[type];
            normalized._originalType = type;
        }
    }
    // LangGraph: tool_calls in additional_kwargs → top level
    if (normalized.additional_kwargs &&
        typeof normalized.additional_kwargs === "object") {
        const additionalKwargs = normalized.additional_kwargs;
        // Handle tool_calls from additional_kwargs
        if (additionalKwargs.tool_calls) {
            if (!normalized.tool_calls) {
                // No top-level tool_calls, extract from additional_kwargs
                normalized.tool_calls = additionalKwargs.tool_calls;
            }
            // IMPORTANT: Always remove tool_calls from additional_kwargs to prevent
            // ChatMlSchema from overwriting our processed version when it spreads additional_kwargs
            // This handles both cases:
            // 1. LangGraph format: tool_calls extracted from additional_kwargs
            // 2. LangChain format: top-level tool_calls already exists, prevent overwrite
            // Don't mutate original object - JSON view needs to show original data
            // Filter out null fields to prevent them from creating extra json blocks in UI
            const { tool_calls: _removed, ...restKwargs } = additionalKwargs;
            normalized.additional_kwargs = (0, helpers_1.removeNullFields)(restKwargs);
        }
        // Keep other fields in additional_kwargs (non-null values) for JSON view
        // ChatMlSchema will spread these into passthrough json field
    }
    // Flatten nested tool_calls format to flat ChatML format
    // LangGraph can have OpenAI-style nested format: {id, type, function: {name, arguments}}
    // Convert to flat format: {id, name, arguments, type}
    if (normalized.tool_calls && Array.isArray(normalized.tool_calls)) {
        const sanitizedToolCalls = normalized.tool_calls.filter((tc) => Boolean(tc) && typeof tc === "object");
        normalized.tool_calls = sanitizedToolCalls.map((tc) => {
            // If nested format (has function.name), flatten it
            if (tc.function && typeof tc.function === "object") {
                const func = tc.function;
                return {
                    id: tc.id,
                    name: func.name,
                    arguments: typeof func.arguments === "string"
                        ? func.arguments
                        : JSON.stringify(func.arguments ?? {}),
                    type: tc.type || "function",
                    ...(tc.index !== undefined ? { index: tc.index } : {}),
                };
            }
            // Already flat format - handle both 'args' (LangChain) and 'arguments' (OpenAI)
            // LangChain uses 'args', OpenAI uses 'arguments'
            const argsValue = tc.args ?? tc.arguments;
            return {
                ...tc,
                name: tc.name,
                arguments: typeof argsValue === "string"
                    ? argsValue
                    : JSON.stringify(argsValue ?? {}),
            };
        });
    }
    // For tool messages with rich object content, spread into message
    // so it goes to json passthrough field → renders as PrettyJsonView.
    // Rich = nested structure OR 3+ keys. Simple <=2 scalar keys.
    if (normalized.role === "tool" &&
        typeof normalized.content === "object" &&
        normalized.content !== null &&
        !Array.isArray(normalized.content)) {
        if ((0, helpers_1.isRichToolResult)(normalized.content)) {
            // Rich object: spread for table rendering
            const { content, ...rest } = normalized;
            return { ...rest, ...content };
        }
        // Simple object: stringify for text rendering
        normalized.content = (0, helpers_1.stringifyToolResultContent)(normalized.content);
    }
    return normalized;
}
function filterAndNormalizeMessages(data) {
    return data
        .filter((msg) => !isLangGraphToolDefinition(msg))
        .map(normalizeMessage);
}
function preprocessData(data) {
    if (!data)
        return data;
    // Array of messages
    if (Array.isArray(data)) {
        const extractedTools = extractToolDefinitions(data);
        const normalizedMessages = filterAndNormalizeMessages(data);
        if (extractedTools.length > 0) {
            // Attach tools to all messages
            return (0, helpers_1.attachToolDefinitionsToMessages)(normalizedMessages, extractedTools);
        }
        return normalizedMessages;
    }
    // Object with messages key
    if (typeof data === "object" && "messages" in data) {
        const obj = data;
        if (Array.isArray(obj.messages)) {
            const extractedTools = extractToolDefinitions(obj.messages);
            const rootTools = (0, helpers_1.normalizeToolDefinitionsForChatMl)(obj.tools);
            const tools = [...extractedTools, ...rootTools];
            const normalizedMessages = filterAndNormalizeMessages(obj.messages);
            if (tools.length > 0) {
                return {
                    ...obj,
                    messages: (0, helpers_1.attachToolDefinitionsToMessages)(normalizedMessages, tools),
                };
            }
            return {
                ...obj,
                messages: normalizedMessages,
            };
        }
    }
    // Single message - wrap in array for consistency
    if (typeof data === "object" && ("role" in data || "type" in data)) {
        return [normalizeMessage(data)];
    }
    return data;
}
exports.langgraphAdapter = {
    id: "langgraph",
    detect(ctx) {
        const meta = (0, helpers_1.parseMetadata)(ctx.metadata);
        // EXPLICIT: Framework hint
        if (ctx.framework === "langgraph")
            return true;
        // REJECTIONS: Reject AI SDK v5, OpenAI Agents SDK, Semantic Kernel and Pydantic formats
        if (meta && typeof meta === "object") {
            // Check scope.name for AI SDK, OpenAI Agents, or Semantic Kernel
            if ("scope" in meta &&
                meta.scope !== null &&
                typeof meta.scope === "object") {
                const scope = meta.scope;
                // Reject AI SDK v5 (scope.name === "ai")
                if (scope.name === "ai")
                    return false;
                // Reject OpenAI Agents SDK
                if (scope.name === "openinference.instrumentation.openai_agents" ||
                    (typeof scope.name === "string" &&
                        scope.name.includes("openai_agents"))) {
                    return false;
                }
                if (typeof scope.name === "string" &&
                    scope.name.startsWith("Microsoft.SemanticKernel")) {
                    return false;
                }
                if (scope?.name === "pydantic-ai")
                    return false;
            }
            // Reject AI SDK metadata
            if (meta["scope.name"] === "ai")
                return false;
            // Check attributes["operation.name"] for AI SDK pattern
            if ("attributes" in meta && typeof meta.attributes === "object") {
                const attrs = meta.attributes;
                if (attrs &&
                    typeof attrs["operation.name"] === "string" &&
                    attrs["operation.name"].startsWith("ai.")) {
                    return false;
                }
            }
            const flatOperationName = meta["attributes.operation.name"];
            if (typeof flatOperationName === "string" &&
                flatOperationName.startsWith("ai.")) {
                return false;
            }
            const flatAiOperationId = meta["attributes.ai.operationId"];
            if (typeof flatAiOperationId === "string" &&
                flatAiOperationId.startsWith("ai.")) {
                return false;
            }
        }
        // HINTS: LangGraph/LangChain-specific metadata markers
        if (meta && typeof meta === "object") {
            // LangGraph markers
            if ("langgraph_step" in meta ||
                "langgraph_node" in meta ||
                "langgraph_path" in meta ||
                "langgraph_checkpoint_ns" in meta ||
                meta.framework === "langgraph" ||
                (Array.isArray(meta.tags) && meta.tags.includes("langgraph"))) {
                return true;
            }
            // LangSmith/LangChain markers (ls_ prefix indicates LangChain ecosystem)
            const hasLangChainMarkers = Object.keys(meta).some((key) => key.startsWith("ls_"));
            if (hasLangChainMarkers) {
                return true;
            }
        }
        // STRUCTURAL: Schema-based detection on metadata
        if (LangChainMessageSchema.safeParse(ctx.metadata).success)
            return true;
        if (LangGraphMessageSchema.safeParse(ctx.metadata).success)
            return true;
        // Check wrapped messages format
        if (LangGraphWrappedSchema.safeParse(ctx.metadata).success) {
            const wrapped = ctx.metadata;
            // reject OpenAI Chat Completions format {tools: [...], messages: [...]}
            if (Array.isArray(wrapped.tools)) {
                return false;
            }
            if (LangChainMessageSchema.safeParse(wrapped.messages).success)
                return true;
            if (LangGraphMessageSchema.safeParse(wrapped.messages).success)
                return true;
        }
        // finally Schema-based detection on data b/c of performance
        if (LangChainMessageSchema.safeParse(ctx.data).success)
            return true;
        if (LangGraphMessageSchema.safeParse(ctx.data).success)
            return true;
        // Check wrapped messages format on data
        if (LangGraphWrappedSchema.safeParse(ctx.data).success) {
            const wrapped = ctx.data;
            // reject OpenAI Chat Completions format {tools: [...], messages: [...]}
            if (Array.isArray(wrapped.tools)) {
                return false;
            }
            if (LangChainMessageSchema.safeParse(wrapped.messages).success)
                return true;
            if (LangGraphMessageSchema.safeParse(wrapped.messages).success)
                return true;
        }
        return false;
    },
    preprocess(data, _kind, _ctx) {
        return preprocessData(data);
    },
};
